/* eslint-disable @typescript-eslint/no-explicit-any */
import { APP_INITIALIZER, NgModule, isDevMode } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpClientModule, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { environment } from '../environments/environment';
import { LayoutsModule } from './layouts/layouts.module';
import { ExtrapagesModule } from './extrapages/extrapages.module';
import { ErrorInterceptor } from './core/helpers/error.interceptor';
import { API_URL, BaseUrlInterceptor } from './core/interceptors/base-url-interceptor.interceptor';
import { NgxPermissionsModule } from 'ngx-permissions';
import { TokenInterceptor } from './core/interceptors/token.interceptor';
import { StoreModule } from '@ngrx/store';
import { reducers } from '@app/core/store/reduceIndex';
import { EffectsModule } from '@ngrx/effects';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule, ToastrService } from 'ngx-toastr';
import * as envStage from '@env/environment.stage';
import { ServiceWorkerModule } from '@angular/service-worker';
import { BusinessPortalService } from './business-portal/business-portal-services/business-portal.service';
import { TokenService } from './core/services/token.service';
import { firstValueFrom } from 'rxjs';

// The dictionary comes from the active build's backend; the stage URL stays the fallback for a build whose
// publicUrl is empty (production serves same-origin), so the live portal behaves exactly as before.
export function createTranslateLoader(http: HttpClient): any {
  const base = environment.publicUrl || envStage.environment.publicUrl;
  return new TranslateHttpLoader(http, `${base}assets/languages/`, '.json');
}

export function tokenGetter() {
  return JSON.parse(localStorage.getItem(environment.tokenKey) ?? '{}').accessToken;
}

function loadPermissionsAtReload(
  businessPortalService: BusinessPortalService,
  toast: ToastrService,
  tokenService: TokenService,
) {
  return () => {
    if (
      location.pathname.includes('business-portal') &&
      tokenService.getTokens().businessportalAccessToken &&
      tokenService.getTokens().businessportalRefreshToken
    ) {
      return firstValueFrom(businessPortalService.getBusinessDetail())
        .then((response) => {
          if (response.status) {
            businessPortalService.setBusinessDetails(response.data);
          } else {
            toast.error(response.message);
          }
        })
        .catch((error) => {
          toast.error(error.message);
          console.error('Error loading permissions:', error);
        });
    }
    return Promise.resolve();
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    BrowserModule,
    AppRoutingModule,
    //LeafletModule,
    LayoutsModule,
    NgxPermissionsModule.forRoot(),
    NgbModule,
    HttpClientModule,
    ExtrapagesModule,
    BrowserAnimationsModule,
    ToastrModule.forRoot({
      maxOpened: 2,
    }),
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot([]),
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: loadPermissionsAtReload,
      multi: true,
      deps: [BusinessPortalService, ToastrService, TokenService],
    },
    { provide: API_URL, useValue: environment.apiUrl },
    { provide: HTTP_INTERCEPTORS, useClass: BaseUrlInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
